import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { useField } from 'formik';
import { selectStatusByName } from '@redux/selectors/FormSelector';
import { selectListingFormData, selectAgentFormData, selectOfficeFormData } from '@redux/selectors/FormDataSelector';

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import FormikTextInput from './formikComponents/FormikTextInput';
import FormikBoolean from './formikComponents/FormikBoolean';
import FormikDatePicker from './formikComponents/FormikDatePicker';
import FormikNumberFormatInput from './formikComponents/FormikNumberFormatInput';
import FormikSelect from './formikComponents/FormikSelect';

const FormikField = props => {
  const {
    FieldName,
    helpText,
    inputType,
    showHelpIcon,
    datatype,
    IsVisible,
    IsDisabled,
    name,
    fieldLabel,
    validateFigField = () => null,
    repeatableType = '',
    repeatableId = '',
  } = props;

  const [field, , helpers] = useField(FieldName);
  const [localFieldValue, setLocalFieldValue] = useState(field?.value);
  const status = useSelector(state => selectStatusByName(state, name, repeatableType, repeatableId)); // validation result
  const listingFormData = useSelector(selectListingFormData);
  const agentFormData = useSelector(selectAgentFormData);
  const officeFormData = useSelector(selectOfficeFormData);
  const fieldIsVisible = IsVisible;
  const fieldIsDisabled = IsDisabled ?? false;
  let currentComponentInitialValue = null;

  // need to be able to supply hydrate value after component load.
  const CurrentComponent = useMemo(() => {
    const textInputWhitelist = ['string'];
    const NumericInputWhitelist = ['integer', 'decimal', 'numeric'];
    const toggleInputWhiteList = ['boolean', 'bit'];
    const datePickerWhitelist = ['datetime', 'date', 'time'];

    if (inputType === 'TextInput' && textInputWhitelist.includes(datatype)) return FormikTextInput;
    if (inputType === 'ComboBox') return FormikSelect;
    if (toggleInputWhiteList.includes(datatype)) return FormikBoolean;
    if (datePickerWhitelist.includes(datatype)) {
      if (listingFormData) {
        currentComponentInitialValue = listingFormData[FieldName];
      } else if (agentFormData) {
        currentComponentInitialValue = agentFormData[FieldName];
      } else if (officeFormData) {
        currentComponentInitialValue = officeFormData[FieldName];
      }
      return FormikDatePicker;
    }
    if (NumericInputWhitelist.includes(datatype)) return FormikNumberFormatInput;

    return () => null;
  }, [inputType, datatype]);

  const updateFormikOnBlur = useMemo(() => {
    const textInputWhitelist = ['string'];
    const NumericInputWhitelist = ['integer', 'decimal', 'numeric'];
    return ((inputType === 'TextInput' && textInputWhitelist.includes(datatype)) || (NumericInputWhitelist.includes(datatype)));
  }, [inputType, datatype]);

  const sameValue = (oldValue, newValue) => {
    if (oldValue === newValue) return true;
    if (oldValue?.toString() === newValue?.toString()) return true;
    if (!oldValue && !newValue) return true; // TODO: Should this mimic FormikInputGroup?
    return false;
  };

  const validateFieldName = useMemo(() => {
    if (repeatableType !== '') return name;
    return FieldName;
  }, [repeatableType]);

  const handleBlur = e => {
    if (updateFormikOnBlur) {
      if (sameValue(field?.value, e.target.value)) return;
      helpers.setValue(e.target.value);

      validateFigField(validateFieldName, e.target.value);
    }
  };

  const handleChange = e => {
    if (updateFormikOnBlur) {
      setLocalFieldValue(e.target.value);
    } else {
      setLocalFieldValue(e);
      helpers.setValue(e);
      validateFigField(validateFieldName, e);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  useEffect(() => {
    if (field?.value == null || field.value === localFieldValue) return;
    setLocalFieldValue(field.value);
  }, [field?.value]);

  if (!fieldIsVisible) return null;

  return (
    <div className="form-group" id={fieldLabel}>
      <label
        className="form-label"
        htmlFor={inputType}
      >
        {fieldLabel}
        {status?.errorMessage && <span className="invalid-feedback">&nbsp;*</span>}
      </label>
      {
        showHelpIcon && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${FieldName}-input-help`} style={{ margin: 0 }}>{helpText}</Tooltip>}
          >
            <i className="fal fa-question-circle float-right" />
          </OverlayTrigger>
        )
      }
      <CurrentComponent
        {...props}
        name={FieldName}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        value={localFieldValue}
        disabled={fieldIsDisabled}
        initialValue={currentComponentInitialValue}
      />
      {status?.errorMessage && <div className="invalid-feedback">{status?.errorMessage}</div>}
    </div>
  );
};

FormikField.propTypes = {
  FieldName: PropTypes.string,
  helpText: PropTypes.string,
  inputType: PropTypes.string,
  showHelpIcon: PropTypes.bool,
  datatype: PropTypes.string,
  IsVisible: PropTypes.bool,
  IsDisabled: PropTypes.bool,
  name: PropTypes.string,
  fieldLabel: PropTypes.string,
  validateFigField: PropTypes.func,
  repeatableType: PropTypes.string,
  repeatableId: PropTypes.string,
};

export default FormikField;
